import { createContext, PropsWithChildren, useContext } from "react";
import { ScopedBus } from "types/busType";
import { useFavoriteBuses } from "./FavoriteBusesContext";
import useFavoriteScopedBuses from "./useFavoriteScopedBuses";

// This region's favorites fetched by id, so they are present even when the map
// query filters them out. Held in a provider so the panel, the map and the
// selection all read one fetch.
const FavoriteScopedBusesContext = createContext<
    ReadonlyArray<ScopedBus> | undefined
>(undefined);

// Separate from the list so the map and the selection, which only read the
// list, do not re-render on loading flips.
const FavoriteScopedBusesLoadingContext = createContext<boolean | undefined>(
    undefined
);

const FavoriteScopedBusesProvider: React.FC<PropsWithChildren<unknown>> = ({
    children
}: PropsWithChildren<unknown>) => {
    const { favorites } = useFavoriteBuses();
    const { scopedBuses, isLoading } = useFavoriteScopedBuses(favorites);

    return (
        <FavoriteScopedBusesContext.Provider value={scopedBuses}>
            <FavoriteScopedBusesLoadingContext.Provider value={isLoading}>
                {children}
            </FavoriteScopedBusesLoadingContext.Provider>
        </FavoriteScopedBusesContext.Provider>
    );
};

const useFavoriteScopedBusesContext = (): ReadonlyArray<ScopedBus> => {
    const context = useContext(FavoriteScopedBusesContext);
    if (context === undefined) {
        throw new Error(
            "useFavoriteScopedBusesContext must be used within a FavoriteScopedBusesProvider"
        );
    }

    return context;
};

// Whether the by-id fetch behind useFavoriteScopedBusesContext is in flight.
const useFavoriteScopedBusesLoading = (): boolean => {
    const context = useContext(FavoriteScopedBusesLoadingContext);
    if (context === undefined) {
        throw new Error(
            "useFavoriteScopedBusesLoading must be used within a FavoriteScopedBusesProvider"
        );
    }

    return context;
};

export {
    FavoriteScopedBusesProvider,
    useFavoriteScopedBusesContext,
    useFavoriteScopedBusesLoading
};
